import { get } from '../../network';
import { interpolateString } from '../../strings';
import type { ReverseGeocodeResponse } from '@dfg/brahma-types';
import type { Coordinates } from './index.types';
import {
  GEOTAG_CONFIG,
  CHAR_WIDTH_MULTIPLIER,
  IMAGE_MIME_TYPES,
  MAX_GEOTAGGED_FILE_SIZE_MB,
  PRE_COMPRESSION_THRESHOLD_MB,
  COMPRESSION_QUALITY
} from './index.constants';
import { COMMON_API_URLS } from '../../constants/apis';
import { formatDateWithTimeAndTimezone } from '../../date-time';
import { compressImage } from '../compress-image';

/**
 * Load image from URL asynchronously
 * For blob URLs (from File objects), crossOrigin is not needed
 * For external URLs, crossOrigin = 'anonymous' is required to allow the image to be drawn on canvas
 */
const loadImage = async (
  url: string,
  useCors: boolean = false
): Promise<HTMLImageElement> => {
  return await new Promise((resolve, reject) => {
    const img = new Image();
    if (useCors) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => {
      resolve(img);
    };
    img.onerror = reject;
    img.src = url;
  });
};

/**
 * Fetch asset and return blob URL
 */
const fetchAsset = async (path: string): Promise<string> => {
  try {
    const url = `${GEOTAG_CONFIG.STATIC_ASSETS_URL}${path}`;
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) {
      throw new Error(`Failed to fetch asset: ${response.status}`);
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    // Log error but don't fail geotagging - assets are optional
    console.warn(`Failed to fetch geotag asset ${path}:`, error);
    throw error;
  }
};

/**
 * Calculate responsive layout dimensions (matching backend logic)
 */
const calculateGeotagLayout = (imageWidth: number) => {
  const fontSize = Math.max(
    GEOTAG_CONFIG.MIN_FONT_SIZE,
    Math.floor(imageWidth * GEOTAG_CONFIG.FONT_SIZE_BASE_RATIO)
  );
  const padding = Math.max(
    GEOTAG_CONFIG.MIN_PADDING,
    Math.floor(imageWidth * GEOTAG_CONFIG.PADDING_RATIO)
  );
  const lineHeight = Math.floor(
    fontSize * GEOTAG_CONFIG.LINE_HEIGHT_MULTIPLIER
  );
  const mapIconWidth = Math.floor(
    imageWidth * GEOTAG_CONFIG.MAP_ICON_WIDTH_RATIO
  );
  const textStartX = padding + mapIconWidth + GEOTAG_CONFIG.SPACING;

  const maxTextWidth = imageWidth - textStartX - padding;

  return {
    fontSize,
    padding,
    lineHeight,
    mapIconWidth,
    textStartX,
    maxTextWidth
  };
};

/**
 * Fetch reverse geocode address from location service
 * Returns null if the API call fails - geotagging will continue without address info
 */
const fetchReverseGeocode = async (
  coordinates: Coordinates
): Promise<ReverseGeocodeResponse | null> => {
  try {
    const url = interpolateString(COMMON_API_URLS.REVERSE_GEOCODE, {
      latitude: String(coordinates.latitude),
      longitude: String(coordinates.longitude)
    });

    const response = (await get(url)) as { data: ReverseGeocodeResponse };
    return response?.data?.city && response?.data?.fullAddress
      ? response.data
      : null;
  } catch (error) {
    // Silently fail - geotagging will continue without address info
    // This is expected if the reverse-geocode endpoint doesn't exist
    return null;
  }
};

/**
 * Break address into multiple lines based on width constraints
 */
const breakAddressIntoLines = (
  fullAddress: string,
  maxTextWidth: number,
  fontSize: number
): string[] => {
  const addressLines: string[] = [];
  const words = fullAddress.split(' ');
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const estimatedWidth = testLine.length * fontSize * CHAR_WIDTH_MULTIPLIER;

    if (estimatedWidth <= maxTextWidth) {
      currentLine = testLine;
    } else {
      if (currentLine) addressLines.push(currentLine);
      currentLine = word;
    }
  }

  if (currentLine) addressLines.push(currentLine);
  return addressLines;
};

/**
 * Create text overlay canvas with responsive sizing - matches backend format
 */
const createTextOverlay = (
  imageWidth: number,
  coords: Coordinates,
  timestamp: number,
  addressInfo: ReverseGeocodeResponse | null
): { dataUrl: string; height: number } => {
  const layout = calculateGeotagLayout(imageWidth);
  const { fontSize, padding, lineHeight, textStartX, maxTextWidth } = layout;

  // Calculate coordinates text
  const coordinatesText = `Lat ${coords.latitude.toFixed(
    10
  )} Long ${coords.longitude.toFixed(10)}`;
  const dateText = formatDateWithTimeAndTimezone(timestamp);

  // Calculate address lines if address info is available
  let addressLines: string[] = [];
  let cityText = '';

  if (addressInfo) {
    cityText = addressInfo.city;
    addressLines = breakAddressIntoLines(
      addressInfo.fullAddress,
      maxTextWidth,
      fontSize
    );
  }

  // Calculate overlay height based on content
  // City (1 line) + Address lines + Coordinates (1 line) + Date (1 line)
  const textContentHeight = fontSize + (addressLines.length + 2) * lineHeight;
  const overlayHeight = padding + textContentHeight + padding;

  const canvas = document.createElement('canvas');
  canvas.width = imageWidth;
  canvas.height = overlayHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');

  // Black background
  ctx.fillStyle = GEOTAG_CONFIG.OVERLAY_BG;
  ctx.fillRect(0, 0, imageWidth, overlayHeight);

  // White text
  ctx.fillStyle = GEOTAG_CONFIG.TEXT_COLOR;
  ctx.font = `${fontSize}px Arial, sans-serif`;

  let y = padding + fontSize;

  // City (larger font if address info available)
  if (cityText) {
    ctx.font = `${fontSize + 4}px Arial, sans-serif`;
    ctx.fillText(cityText, textStartX, y);
    ctx.font = `${fontSize}px Arial, sans-serif`;
    y += lineHeight;

    // Address lines
    for (const line of addressLines) {
      ctx.fillText(line, textStartX, y);
      y += lineHeight;
    }
  }

  // Coordinates
  ctx.fillText(coordinatesText, textStartX, y);
  y += lineHeight;

  // Date
  ctx.fillText(dateText, textStartX, y);

  return {
    dataUrl: canvas.toDataURL(IMAGE_MIME_TYPES.PNG),
    height: overlayHeight
  };
};

/**
 * Resize asset image to target dimensions
 */
const resizeAsset = async (
  assetUrl: string,
  targetWidth: number,
  targetHeight: number
): Promise<string> => {
  // External assets need CORS
  const img = await loadImage(assetUrl, true);
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Failed to get canvas context');

  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
  return canvas.toDataURL(IMAGE_MIME_TYPES.PNG);
};

/**
 * Main geotagging function - responsive approach matching backend
 */
export const geotagImage = async (
  file: File,
  coordinates: Coordinates,
  captureTime: number
): Promise<File> => {
  try {
    // Skip non-images
    if (!file.type.startsWith('image/')) {
      return file;
    }

    // Stage 1: Pre-compress original image if it exceeds threshold (6MB)
    // This prevents merged images from exceeding 10MB limit after adding overlays
    let imageUrl = URL.createObjectURL(file);

    // Try to load image - if it fails (e.g., unsupported format), return original
    let img: HTMLImageElement;
    try {
      img = await loadImage(imageUrl, false);
    } catch (error) {
      URL.revokeObjectURL(imageUrl);
      console.warn(
        'Failed to load image for geotagging, returning original file:',
        error
      );
      return file;
    }
    let width = img.naturalWidth || img.width;
    let height = img.naturalHeight || img.height;

    // If original image exceeds pre-compression threshold, compress it first
    // This is important because after adding overlays (logo, text, map icon),
    // even a 6-8MB image can exceed 10MB
    if (file.size > PRE_COMPRESSION_THRESHOLD_MB * 1024 * 1024) {
      // Create blob from file for compression
      const originalBlob = new Blob([file], { type: file.type });

      // Compress the original image to stay under threshold
      const compressedBlob = await compressImage(originalBlob, {
        maxSizeInMB: PRE_COMPRESSION_THRESHOLD_MB
      });

      // Revoke the original URL as we'll create a new one
      URL.revokeObjectURL(imageUrl);

      // Update to use compressed image
      imageUrl = URL.createObjectURL(compressedBlob);
      img = await loadImage(imageUrl, false);
      width = img.naturalWidth || img.width;
      height = img.naturalHeight || img.height;
    }

    // Fetch reverse geocode address from Garuda BFF
    const addressInfo = await fetchReverseGeocode(coordinates);

    // Calculate responsive layout
    const layout = calculateGeotagLayout(width);
    const { padding, mapIconWidth } = layout;

    // Calculate logo dimensions (responsive)
    const logoWidth = Math.max(
      Math.floor(width * GEOTAG_CONFIG.LOGO_WIDTH_RATIO),
      GEOTAG_CONFIG.MIN_LOGO_WIDTH
    );
    const logoHeight = Math.floor(logoWidth * GEOTAG_CONFIG.LOGO_HEIGHT_RATIO);

    // Create text overlay with address info
    const { dataUrl: textOverlay, height: overlayHeight } = createTextOverlay(
      width,
      coordinates,
      captureTime,
      addressInfo
    );
    const mapIconHeight = overlayHeight - padding * 2;

    // Fetch assets in parallel - continue even if assets fail to load
    const [mapIconBlobUrl, logoBlobUrl] = await Promise.all([
      fetchAsset(GEOTAG_CONFIG.MAP_ICON_PATH).catch((error) => {
        console.warn('Map icon failed to load, continuing without it:', error);
        return null;
      }),
      fetchAsset(GEOTAG_CONFIG.LOGO_PATH).catch((error) => {
        console.warn('Logo failed to load, continuing without it:', error);
        return null;
      })
    ]);

    // Resize assets to match calculated dimensions
    const [mapIconUrl, logoUrl] = await Promise.all([
      mapIconBlobUrl
        ? resizeAsset(mapIconBlobUrl, mapIconWidth, mapIconHeight).catch(
            (error) => {
              console.warn('Map icon resize failed:', error);
              return null;
            }
          )
        : Promise.resolve(null),
      logoBlobUrl
        ? resizeAsset(logoBlobUrl, logoWidth, logoHeight).catch((error) => {
            console.warn('Logo resize failed:', error);
            return null;
          })
        : Promise.resolve(null)
    ]);

    // Calculate logo position (centered, above overlay with margin)
    const logoX = Math.floor((width - logoWidth) / 2);
    const logoY = height - logoHeight - GEOTAG_CONFIG.LOGO_BOTTOM_MARGIN;

    // Merge all layers directly to WebP format for better compression
    // Load base image and text overlay (required)
    const [baseImg, textOverlayImg] = await Promise.all([
      loadImage(imageUrl, false),
      loadImage(textOverlay, false)
    ]);

    // Load optional assets (only if they were successfully fetched and resized)
    let logoImg: HTMLImageElement | null = null;
    let mapIconImg: HTMLImageElement | null = null;

    if (logoUrl) {
      try {
        logoImg = await loadImage(logoUrl, true);
      } catch (error) {
        console.warn('Logo image load failed:', error);
      }
    }

    if (mapIconUrl) {
      try {
        mapIconImg = await loadImage(mapIconUrl, true);
      } catch (error) {
        console.warn('Map icon image load failed:', error);
      }
    }

    // Create canvas and merge all layers directly to WebP
    const mergeCanvas = document.createElement('canvas');
    mergeCanvas.width = width;
    mergeCanvas.height = height + overlayHeight;
    const mergeCtx = mergeCanvas.getContext('2d');
    if (!mergeCtx) throw new Error('Failed to get canvas context for merging');

    // Use high-quality image smoothing
    mergeCtx.imageSmoothingEnabled = true;
    mergeCtx.imageSmoothingQuality = 'high';

    // Draw base image
    mergeCtx.drawImage(baseImg, 0, 0);

    // Draw logo (if loaded)
    if (logoImg) {
      mergeCtx.drawImage(logoImg, logoX, logoY);
    }

    // Draw text overlay
    mergeCtx.drawImage(textOverlayImg, 0, height);

    // Draw map icon (if loaded)
    if (mapIconImg) {
      mergeCtx.drawImage(mapIconImg, padding, height + padding);
    }

    // Convert directly to WebP format with quality setting
    const webpDataUrl = mergeCanvas.toDataURL(
      IMAGE_MIME_TYPES.WEBP,
      COMPRESSION_QUALITY
    );
    const response = await fetch(webpDataUrl);
    let finalBlob = await response.blob();

    // Stage 2: Post-compress merged image if it still exceeds the 10MB limit
    // Even after pre-compression and WebP conversion, merged image might exceed limit
    if (finalBlob.size > MAX_GEOTAGGED_FILE_SIZE_MB * 1024 * 1024) {
      finalBlob = await compressImage(finalBlob, {
        maxSizeInMB: MAX_GEOTAGGED_FILE_SIZE_MB
      });
    }

    // Update file extension to match WebP format
    const fileName = file.name.replace(/\.[^.]+$/, '') + '.webp';

    const geotaggedFile = new File([finalBlob], fileName, {
      type: IMAGE_MIME_TYPES.WEBP,
      lastModified: Date.now()
    });

    // Cleanup blob URLs
    URL.revokeObjectURL(imageUrl);
    if (mapIconBlobUrl) {
      URL.revokeObjectURL(mapIconBlobUrl);
    }
    if (logoBlobUrl) {
      URL.revokeObjectURL(logoBlobUrl);
    }

    return geotaggedFile;
  } catch (error) {
    throw new Error(
      `Geotagging failed: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  }
};
